import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import TaxonomyPicker from "@/components/TaxonomyPicker";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Self-service specialization editor. Any authenticated user can pick a leaf
 * on the shared research taxonomy — the value is stored as (id, path,
 * path_ids) so admin/employee task-assignment by domain automatically
 * includes this user for the picked node and every ancestor.
 */
export default function AccountSpecialization() {
  const { user } = useAuth();
  const [taxonomyId, setTaxonomyId] = useState(null);
  const [path, setPath] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Reload the fresh user record so we don't stale-cache from context.
    api.get("/auth/me").then((r) => {
      setTaxonomyId(r.data?.specialization_taxonomy_id || null);
      setPath(r.data?.specialization_path || []);
    }).catch(() => {});
  }, [user?.user_id]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/me/specialization", { taxonomy_id: taxonomyId });
      setPath(r.data?.path || []);
      toast.success(taxonomyId ? "Specialization saved" : "Specialization cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="account-specialization-page">
      <Link to="/dashboard" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-ink inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </Link>

      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mt-6">MY PROFILE</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Research <em className="italic text-kb-primary">specialization</em></h1>
      <p className="text-sm text-kb-ink-muted mt-3 max-w-xl">
        Pick the deepest node that best describes your focus (e.g. <em>Computer Science › AI/ML › LLM</em>). Managers can then assign work to everyone under a branch — you&apos;ll receive tasks tagged for your specialization and every ancestor.
      </p>

      <div className="mt-8 border border-slate-200 bg-white rounded-sm p-6">
        <div className="flex items-center gap-2 text-kb-ink">
          <Sparkles className="w-4 h-4 text-kb-primary" />
          <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Current selection</span>
        </div>
        <div className="mt-1 text-sm">
          {path.length === 0 ? (
            <span className="text-kb-ink-muted italic">Not set yet — you won&apos;t appear in domain-based assignments.</span>
          ) : (
            <span className="text-kb-primary font-medium">{path.join(" › ")}</span>
          )}
        </div>

        <div className="mt-6">
          <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Cascading picker</label>
          <div className="mt-2">
            <TaxonomyPicker domain="both" value={taxonomyId} onChange={setTaxonomyId} />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <button onClick={save} disabled={saving} className="kb-btn-primary text-sm" data-testid="account-specialization-save">
            {saving ? "Saving…" : "Save specialization"}
          </button>
          {taxonomyId && (
            <button onClick={() => setTaxonomyId(null)} className="kb-btn-outline text-sm" data-testid="account-specialization-clear">
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
